/**
 * Group "bank": the diamond -> gold exchange of the 'pay' screen ("Обменять алмазы на золото").
 *
 * Original flow (new_interf): 'donate' opens init('pay'), which snapshots PLAYER_DIAM = player.diam and resets
 * DIAM_TO_TRANS = 0 (new_interf.ts ~857-863). 'gold_add' adds one diamond, 'gold_all' takes all PLAYER_DIAM
 * (~5549-5556); render shows gold_add/gold_all only while PLAYER_DIAM - DIAM_TO_TRANS > 0 and gold_pay only while
 * DIAM_TO_TRANS > 0 (~3128-3137). 'gold_pay' (~5558-5563) then does
 *   player.diam.value = player.diam.value - DIAM_TO_TRANS;
 *   player.gold.value = player.gold.value + DIAM_TO_TRANS * 1100;
 * and saves (execute_after 'remove').
 */
import { argsObject, intArg } from '../args';
import { int } from '../as3';
import { RuleError } from '../errors';
import { defineOp } from '../registry';

/** Gold per diamond on the 'pay' screen. */
export const GOLD_PER_DIAMOND = 1100;

export interface BankExchangeArgs {
  /** Diamonds to exchange (the screen's DIAM_TO_TRANS). */
  diamonds: number;
}

export interface BankExchangeResult {
  diamonds: number;
  /** Gold credited. */
  gold: number;
}

export const bankOps = {
  /**
   * bank.exchange { diamonds }: diam -= N, gold += N * 1100.
   * Preconditions (button visibility): N >= 1 (gold_pay needs DIAM_TO_TRANS > 0) and N <= the player's diamonds
   * (gold_add / gold_all never go past PLAYER_DIAM). The original checked N only against the snapshot taken when the
   * screen opened; the rule checks the current balance, which is the same value for a single session.
   */
  'bank.exchange': defineOp<BankExchangeArgs, BankExchangeResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['diamonds']);
      return { diamonds: intArg(o, 'diamonds', { min: 1 }) };
    },
    run(state, { diamonds }) {
      const p = state.player;
      if (diamonds > p.diam) throw new RuleError('not_enough_diam', { have: p.diam, need: diamonds });
      // ProtectedInt.value setters: int32 truncation.
      p.diam = int(p.diam - diamonds);
      p.gold = int(p.gold + diamonds * GOLD_PER_DIAMOND);
      return { state, result: { diamonds, gold: diamonds * GOLD_PER_DIAMOND } };
    },
  }),
};
